import { useDrag, useDrop } from "react-dnd";
import { Switch } from "components/atoms/Switch";
import styled from "styled-components";
import { createElement, useRef } from "react";
import { ColumnInstance } from "react-table";
import cx from "classnames";

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const StyledDropdownMenuItem = styled.div`
  padding-left: ${(props) => props.theme.space[3]};
  padding-right: ${(props) => props.theme.space[3]};
  height: ${(props) => props.theme.space[8]};
  display: flex;
  align-items: center;
  cursor: move;

  &:hover {
    background-color: ${(props) => props.theme.backgroundColorHover};
  }
`;

interface Props<T extends object> {
  column: ColumnInstance<T>;
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  onDragStart: () => void;
  onDragEnd: (didDrop: boolean) => void;
  isHidden: boolean;
  onToggleVisibility: (checked: boolean) => void;
}

export function ColumnDragItem<T extends object>({
  column,
  index,
  moveColumn,
  onDragStart,
  onDragEnd,
  isHidden,
  onToggleVisibility,
}: Props<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: "column",
    // The item factory runs once when the gesture begins — use it to snapshot
    // the pre-drag order so a cancel can be reverted.
    item: () => {
      onDragStart();
      return { type: "column", id: column.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    // Persist (or revert) the final order once, when the drag gesture ends —
    // not on every hover step. monitor.didDrop() distinguishes a real drop on a
    // row from a cancel (ESC / released outside the list).
    end: (_item, monitor) => {
      onDragEnd(monitor.didDrop());
    },
  });

  const [, drop] = useDrop({
    accept: "column",
    hover: (item: DragItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      // Only swap once the cursor has crossed the vertical middle of the target
      // row. Without this guard the item swaps the instant it touches a
      // neighbour, then the moved DOM lands back under the cursor and swaps
      // straight back — the "snaps back to where it was" jitter.
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Dragging downwards: only move when past the middle.
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // Dragging upwards: only move when before the middle.
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveColumn(dragIndex, hoverIndex);
      // Mutate the monitor item so the next hover compares against the new
      // position (standard react-dnd sortable bookkeeping).
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <StyledDropdownMenuItem
      ref={ref}
      className={cx("leading-none text-xs flex items-center justify-between", {
        "opacity-50": isDragging,
      })}
    >
      <div className="leading-none flex items-center gap-2">
        <span className="text-gray-400">⋮⋮</span>
        {column.icon && createElement(column.icon)}
        {typeof column.Header === "string"
          ? column.Header
          : column.render("Header")}
      </div>
      <Switch checked={!isHidden} onCheckedChange={onToggleVisibility} />
    </StyledDropdownMenuItem>
  );
}
